import { isCounted, publishedCount } from './arcSchemas';
import { LINK_PUBLISHED, type Arc } from './arcTypes';

/*
 * What the "Publish the ARC?" dialog warns about, worked out from the
 * working copy (organization-arc group 6, D7, B11): a switch that is on
 * while its link is not published; placed items the app will not show; a
 * visible section that will show nothing; hidden sections. None blocks: the
 * server refuses a publish only by its one rule and the size limits (B10).
 */

export interface PublishWarning {
  /**
   * `donate`, `volunteer`, `items`, `empty-<section id>` or
   * `hidden-<section id>`: the test ID `arc-builder-publish-warning-<kind>-item`.
   */
  kind: string;
  text: string;
}

const BUTTONS = [
  ['donate', 'Donate'],
  ['volunteer', 'Volunteer'],
] as const;

export function publishWarnings(arc: Arc): PublishWarning[] {
  const warnings: PublishWarning[] = [];
  for (const [action, label] of BUTTONS) {
    const { enabled, link_status } = arc.header[action];
    if (enabled && link_status !== LINK_PUBLISHED) {
      warnings.push({
        kind: action,
        text: `The ${label} button is on, but its link is not published. The app will not show it.`,
      });
    }
  }

  const unseen = arc.sections.reduce(
    (count, section) => count + section.items.filter((i) => !isCounted(i)).length,
    0,
  );
  if (unseen === 1) {
    warnings.push({
      kind: 'items',
      text: '1 placed item will not show in the app: it is not published or no longer available.',
    });
  } else if (unseen > 1) {
    warnings.push({
      kind: 'items',
      text: `${String(unseen)} placed items will not show in the app: they are not published or no longer available.`,
    });
  }

  for (const section of [...arc.sections].sort((a, b) => a.position - b.position)) {
    if (!section.visible) {
      warnings.push({ kind: `hidden-${section.id}`, text: `${section.name} is hidden.` });
    } else if (publishedCount(section) === 0) {
      // Counted from the copy's items, as the limits are: the server's
      // `published_item_count` is from the last load and the page's edits
      // do not recompute it.
      warnings.push({
        kind: `empty-${section.id}`,
        text: `${section.name} will show nothing in the app.`,
      });
    }
  }
  return warnings;
}
